import { Alert, Popover, notification } from "antd";
import cuid from "cuid";
import { get, isEmpty } from "lodash";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowBackIos, MdReportGmailerrorred, MdSend } from "react-icons/md";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import TextEditor from "shared/components/TextEditor";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { CONTACT_POINT_SYSTEM, PROFILE_LIST, REGEX } from "utils/constants";
import Attachments from "./widgets/Attachments";
import buildEditorBlocks from "./widgets/buildEditorBlocks";

const View = ({
  project,
  onNext,
  loading,
  attachments,
  setAttachments,
  onBack,
  nextLoading: nextLoadingProp,
  data: costs,
  links,
  isSubmitting,
  imageUploader,
}) => {
  const nextLoading = nextLoadingProp ?? isSubmitting;
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const { user } = useContext(StoreContext);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const editorCore = useRef(null);
  const [telecoms, setTelecoms] = useState(
    get(project, "contact.telecoms", [])
      .filter((telecom) => telecom.system === CONTACT_POINT_SYSTEM.EMAIL)
      .map((telecom) => ({
        label: telecom.value,
        value: telecom.id,
      })),
  );

  const phoneNumber = get(user, "phone");

  const email = get(user, "email");

  const address = get(
    get(user, "organization.telecoms", []).find(
      (telecom) => telecom.system === CONTACT_POINT_SYSTEM.ADDRESS,
    ),
    "value",
    "",
  );

  const EMAIL_CONTENT_KEY =
    get(project, "contact.fields.profile") === PROFILE_LIST.RENAULT_EMPLOYEE &&
    get(project, "contact.fields.eligibleMembership")
      ? "RENAULT"
      : "DEFAULT";

  const useDownloadLinks = !!get(
    user,
    "organization.config.useQuoteDownloadLinks",
  );

  const EMAIL_CONTENT = get(
    DETAIL.TEMPLATES.QUOTE.MAIL.MUTUAL_MOBILITY,
    EMAIL_CONTENT_KEY,
    DETAIL.TEMPLATES.QUOTE.MAIL,
  );

  const defaultValue = useMemo(
    () =>
      buildEditorBlocks({
        emailContent: EMAIL_CONTENT,
        mailTranslations: DETAIL.TEMPLATES.QUOTE.MAIL,
        project,
        costs,
        user,
        phoneNumber,
        email,
        address,
        addLinks: get(project, "fields.addLinks"),
      }),
    [],
  );

  const unlinkAttachment = (id) =>
    setAttachments((prev) => [...prev.filter((item) => item.id !== id)]);

  const onAddEmail = (value) => {
    if (REGEX.EMAIL.test(value)) {
      setTelecoms([...telecoms, { label: value, value: cuid() }]);
      value = "";
    } else {
      notification.error({
        message: DETAIL.TEMPLATES.QUOTE.MAIL.INVALID_EMAIL,
      });
    }
  };

  const onDeleteEmail = (email) => {
    setTelecoms(telecoms.filter(({ value }) => value !== email));
  };

  return (
    <>
      <Header
        key={"header"}
        title={DETAIL.TEMPLATES.QUOTE.TITLE}
        actions={[]}
      />
      <div className="quote-template">
        <Alert
          type="warning"
          showIcon
          icon={<MdReportGmailerrorred size={16} />}
          message={DETAIL.TEMPLATES.QUOTE.WARNING}
        />
        <div className="quote-template__mail unlockable">
          <div className="quote-template__mail-header">
            <Select
              label={DETAIL.TEMPLATES.QUOTE.MAIL.TO}
              mode="tags"
              maxTagCount="responsive"
              value={telecoms}
              options={telecoms}
              onSelect={onAddEmail}
              onDeselect={onDeleteEmail}
            />
            <Input
              label={get(EMAIL_CONTENT, "SUBJECT.label")}
              value={get(EMAIL_CONTENT, "SUBJECT.text")}
              disabled
            />
          </div>
          <div className="quote-template__mail-label">
            {DETAIL.TEMPLATES.QUOTE.MAIL.MESSAGE}
          </div>
          <div className="quote-template__mail-body">
            <TextEditor
              defaultValue={defaultValue}
              ref={editorCore}
              i18n={DETAIL.TEMPLATES.QUOTE.TEXT_EDITOR}
              imageUploader={imageUploader}
            />
          </div>
          <Attachments
            unlinkAttachment={unlinkAttachment}
            loading={loading}
            list={attachments}
            useDownloadLinks={useDownloadLinks}
          />
        </div>
        <div className="d-flex justify--between">
          <Button
            onClick={onBack}
            loading={nextLoading}
            type="secondary--link"
            disabled={project.locked}
          >
            <MdArrowBackIos size={16} />
            {DETAIL.TEMPLATES.COMPARISON.PREVIOUS}
          </Button>
          <div className="popover-wrapper">
            {isEmpty(telecoms) ? (
              <Popover content={DETAIL.TEMPLATES.QUOTE.POPOVER}>
                <Button type="secondary" disabled={true}>
                  {DETAIL.TEMPLATES.QUOTE.SEND}
                  <MdSend size={16} />
                </Button>
              </Popover>
            ) : (
              <Button
                onClick={async () => {
                  const template = await editorCore.current.handleSave();
                  onNext({ telecoms, template });
                }}
                type="secondary"
                disabled={project.expired}
                loading={nextLoading}
              >
                {DETAIL.TEMPLATES.QUOTE.SEND}
                <MdSend size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default View;
